import { useEffect, useState, useContext } from 'react'
import { ModalContext } from '../../../../utils/providers/modal'
import { AlertsContext } from '../../../../utils/providers/alerts'
import moment from 'moment'
import './notes.scss'

// Utility functions
import { db } from '../../../../utils/firebase'

// UI components
import { PlusIcon } from '../../../../utils/svgs'
import Button from '../../../ui/button/button'
import Loading from '../../../app/loading/loading'
import OptionView from './view/view'
import NoteRow from './row/row'

// Returns the HTML markup for the resort global notes
export default function HolidayNotes({ holiday }) {
    const [notes, setNotes] = useState([])
    const [loading, setLoading] = useState(false)
    const [adding, setAdding] = useState(false)
    const [editing, setEditing] = useState(null)

    // Import the modal functionality
    const { showModal } = useContext(ModalContext)

    // Import the alert functionality
    const { pushAlert } = useContext(AlertsContext)

    // On component load
    useEffect(() => {
        // Load the notes
        const unsubscribe = db.collection(`CMS_holidays/${holiday.id}/notes`)
            .orderBy("start_date")
            .onSnapshot((noteDocs) => {
                // Reset the loading state
                setLoading(false)

                // Get a timestamp for now
                const timestampNow = moment().format('X')

                // Loop over the documents found
                noteDocs.docChanges().forEach((optionChange) => {
                    // Get the start_date and end_date fields
                    const {
                        title,
                        text,
                        start_date,
                        end_date
                    } = optionChange.doc.data()

                    // Key for identifying the correct badge to show
                    let periodBadge

                    // Past options 
                    if (start_date.seconds < timestampNow && end_date.seconds < timestampNow) {
                        periodBadge = 'PAST'
                    }

                    // Currently active
                    if (start_date.seconds < timestampNow && end_date.seconds > timestampNow) {
                        periodBadge = 'ACTIVE'
                    }

                    // Upcoming
                    if (start_date.seconds > timestampNow && end_date.seconds > timestampNow) {
                        periodBadge = 'UPCOMING'
                    }

                    // Process some readable versions of the start and end dates
                    const startDateString = moment(start_date.seconds, 'X').format('DD-MMM-YYYY')
                    const endDateString = moment(end_date.seconds, 'X').format('DD-MMM-YYYY')

                    // Added
                    if (optionChange.type === 'added') {
                        setNotes((options) => [
                            ...options,
                            {
                                id: optionChange.doc.id,
                                title,
                                text,
                                start_date: startDateString,
                                start_seconds: start_date.seconds,
                                end_date: endDateString,
                                end_seconds: end_date.seconds,
                                period_state: periodBadge
                            }
                        ])
                    }

                    // Modified
                    if (optionChange.type === 'modified') {
                        setNotes((notes) => {
                            let updatedNotes = [...notes]
                            for (let i in notes) {
                                if (notes[i].id === optionChange.doc.id) {
                                    updatedNotes[i] = {
                                        id: optionChange.doc.id,
                                        title,
                                        text,
                                        start_date: startDateString,
                                        start_seconds: start_date.seconds,
                                        end_date: endDateString,
                                        end_seconds: end_date.seconds,
                                        period_state: periodBadge
                                    }
                                    break
                                }
                            }
                            return updatedNotes
                        })
                    }

                    // Removed
                    if (optionChange.type === 'removed') {
                        setNotes((notes) => {
                            return notes.filter((resortNote) => resortNote.id !== optionChange.doc.id)
                        })
                    }
                })
        })

        // Unmount the listener on component unload
        return () => unsubscribe()
    }, [])

    // Load the edit modal for this particular note
    const editNote = async (noteID, noteDetails) => {
        setEditing({ id: noteID, ...noteDetails })
    }

    // Check to make sure we are good to remove the note before doing so
    const checkDeletion = async (noteID) => {
        showModal({
            type: "ALERT",
            title: "Are you sure?",
            cancel: {
                label: "Cancel",
                action: () => { return null; },
            },
            next: {
                label: "Continue",
                action: () => processDeletion(noteID),
            }
        });
    }

    // Process the note deletion
    const processDeletion = async (noteID) => {
        await db.doc(`CMS_holidays/${holiday.id}/notes/${noteID}`).delete()

        pushAlert({
            type: 'SUCCESS',
            title: 'Resort note removed'
        })
    }

    return (
        <>
            <div className='holiday-pricing-container'>
                {loading && <Loading />}

                {!loading &&
                    <div className='holiday-pricing-options'>
                        <Button
                            label="New note"
                            icon={<PlusIcon />}
                            onClick={() => setAdding(true)} />

                        <small>Showing {notes.length} results</small>

                        {notes.map((note) => (
                            <NoteRow
                                key={note.id}
                                details={{ ...note }}
                                edit={(noteID, noteDetails) => editNote(noteID, noteDetails)}
                                checkDelete={(noteID) => checkDeletion(noteID)} />
                        ))}
                    </div>
                }
            </div>

            {/* Adding a new pricing window */}
            {adding &&  <OptionView holidayID={holiday.id} close={() => setAdding(false)} type='new' />}

            {/* Editing an existing price window */}
            {editing &&  <OptionView holidayID={holiday.id} close={() => setEditing(false)} type='edit' window={editing} />}
        </>
    )
}